// #567 — les natures de projet de la « Cartographie des projets immobiliers » (SitadelCarte.tsx).
//
// Demande de Cyrille (08/10) : « pouvoir cocher sur la cartographie des projets immobiliers,
// pareil que pour les mutations » (#566, `mutationsTypes.ts`). Les quatre cases se lisent dans
// les champs que `/api/urbanisme/permits` sert déjà (table `urban_permits`) :
//   · `nb_logements_collectifs` > 0              → logements collectifs ;
//   · sinon `nb_logements_individuels` > 0        → logements individuels ;
//   · sinon, sans logement, `surface_plancher_non_res` > 0 → locaux d'activité ;
//   · sinon                                      → autres (aucun logement ni surface de locaux,
//     ou des logements sans type renseigné : jamais devinés).
// Exclusive, pour que les quatre cases fassent le total : un permis mixte (logements et
// commerce) se range sous ses logements, comme une vente mixte DVF sous son local principal.
// MÊME RÈGLE, MÊME ORDRE côté serveur : `backend/app/observatoire/sitadel_export.py`
// (`nature_permis`), qui écrit la colonne « Nature (filtre de la carte) » de l'export.
//
// Le filtre se fait dans le navigateur : contrairement à `/dvf-carte` (plafonnée à 20 000
// mutations), la route des permis sert TOUS les permis de la commune — rien n'est tronqué.
import { JETONS } from './sitadelEtats'

export const NATURES_PROJET = ['collectifs', 'individuels', 'locaux', 'autres'] as const
export type NatureProjet = typeof NATURES_PROJET[number]

export const LIBELLES_NATURE: Record<NatureProjet, string> = {
  collectifs: 'Logements collectifs', individuels: 'Logements individuels',
  locaux: "Locaux d'activité", autres: 'Autres',
}
// L'infobulle de la case : ce que SITADEL range sous cette nature.
export const AIDES_NATURE: Record<NatureProjet, string> = {
  collectifs: 'Permis créant au moins un logement collectif (appartements)',
  individuels: 'Permis créant uniquement des logements individuels (maisons)',
  locaux: 'Permis sans logement, avec de la surface de plancher hors habitation (bureaux, commerces, entrepôts, équipements…)',
  autres: 'Permis sans logement ni surface de locaux déclarée (travaux sur existant, démolitions, aménagements…)',
}

// Lot D (décision de Marc du 09/10 à 0 h 20 : « couleur selon la nature ») : la couleur des points
// de la carte, une par case. Lot C (CDC du 09/10 au soir, C5) : tirées des jetons de la charte — la
// thématique Logement pour le collectif, son mélange à 65 % de marine pour l'individuel, la marine
// pour les locaux, le gris atténué pour le reste (plus de violet, d'ambre ni de sarcelle). Le social
// et les grands logements restent des bascules et une ligne de la fiche, plus une couleur.
export const COULEURS_NATURE: Record<NatureProjet, string> = {
  collectifs: JETONS.log, individuels: JETONS.logMarine, locaux: JETONS.marine, autres: JETONS.muted,
}

export interface ChampsNature {
  nb_logements_total?: number | null
  nb_logements_collectifs?: number | null
  nb_logements_individuels?: number | null
  surface_plancher_non_res?: number | null
}

export function naturePermis(p: ChampsNature): NatureProjet {
  if ((p.nb_logements_collectifs ?? 0) > 0) return 'collectifs'
  if ((p.nb_logements_individuels ?? 0) > 0) return 'individuels'
  if ((p.nb_logements_total ?? 0) > 0) return 'autres'
  if ((p.surface_plancher_non_res ?? 0) > 0) return 'locaux'
  return 'autres'
}

/** La valeur du paramètre `natures` de l'export, dans l'ordre de la liste ; `null` quand les
 *  quatre sont cochées (tout exporter). */
export function paramNatures(coches: readonly NatureProjet[]): string | null {
  const choisies = NATURES_PROJET.filter(n => coches.includes(n))
  return choisies.length === NATURES_PROJET.length ? null : choisies.join(',')
}

/** Du plus récent au plus ancien ; à date égale, par numéro (le même ordre que l'export). */
export function trierProjets<T extends { date_autorisation?: string | null; num_dau: string }>(liste: readonly T[]): T[] {
  return [...liste].sort((a, b) => {
    const da = a.date_autorisation || '', db = b.date_autorisation || ''
    if (da !== db) return da < db ? 1 : -1
    return a.num_dau < b.num_dau ? -1 : a.num_dau > b.num_dau ? 1 : 0
  })
}

/** « 12/03/2025 » — la date d'autorisation telle que servie (AAAA-MM-JJ), sans fuseau. */
export function dateFr(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '')
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (iso || '–')
}
